import React, { useEffect, useMemo, useState } from "react";
import { YAxis } from "interfaces/chart";
import { TextField, FormControl, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

const convertValueToNumber = (value: string): number | null => {
  let convertedValue = Number(parseFloat(value).toFixed(1));
  return isNaN(convertedValue) ? null : convertedValue;
};

export interface YAxisRangeProps {
  selectedYAxis: YAxis | null;
  /** Callback which is fired when the range values are changed */
  onChangeYAxisMin: (min: number | null) => void;
  onChangeYAxisMax: (max: number | null) => void;
}

export const YAxisRange = ({
  selectedYAxis,
  onChangeYAxisMin,
  onChangeYAxisMax
}: YAxisRangeProps): JSX.Element => {
  const [localMin, setLocalMin] = useState<number | null>(null);
  const [localMax, setLocalMax] = useState<number | null>(null);
  const [minTimer, setMinTimer] = useState<ReturnType<typeof setTimeout>>();
  const [maxTimer, setMaxTimer] = useState<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocalMin(selectedYAxis?.min ?? null);
  }, [selectedYAxis?.min]);

  useEffect(() => {
    setLocalMax(selectedYAxis?.max ?? null);
  }, [selectedYAxis?.max]);

  useEffect(() => {
    // Clear the previous timer when a new one is set and when component unmounts.
    return () => {
      minTimer && clearTimeout(minTimer);
    };
  }, [minTimer]);

  useEffect(() => {
    // Clear the previous timer when a new one is set and when component unmounts.
    return () => {
      maxTimer && clearTimeout(maxTimer);
    };
  }, [maxTimer]);

  /** Changes the min value of the y-axis both locally and externally.
   * Local update is immediate, but external updates are debounced.
   * @param min the new minimum value of the y-axis
   */
  const handleChangeMin = (min: string): void => {
    const value = convertValueToNumber(min);
    setLocalMin(value);
    setMinTimer(() => {
      return setTimeout(() => {
        onChangeYAxisMin(value);
      }, 500);
    });
  };

  /** Changes the max value of the y-axis both locally and externally.
   * Local update is immediate, but external updates are debounced.
   * @param max the new maximum value of the y-axis
   */
  const handleChangeMax = (max: string): void => {
    const value = convertValueToNumber(max);
    setLocalMax(value);
    setMaxTimer(() => {
      return setTimeout(() => {
        onChangeYAxisMax(value);
      }, 500);
    });
  };

  const { minError, maxError } = useMemo(() => {
    let minError = false;
    let maxError = false;

    if (selectedYAxis?.min && selectedYAxis?.max) {
      minError = !(selectedYAxis?.min <= selectedYAxis?.max);
      maxError = !(selectedYAxis?.min <= selectedYAxis?.max);
    }
    return { minError: minError, maxError: maxError };
  }, [selectedYAxis]);

  return (
    <AxisRangeWrapper>
      <SubHeader variant="caption">Axis Range</SubHeader>
      <InputWrapper>
        <FormControl error variant="outlined" fullWidth>
          <TextField
            name="yAxisRangeMin"
            label="Min"
            aria-label="yAxisRangeMinInput"
            variant="outlined"
            type="number"
            size="small"
            value={typeof localMin === "number" ? localMin : ""}
            onChange={event => handleChangeMin(event.target.value)}
            InputProps={{
              inputProps: { max: localMax || undefined, step: 0.1 }
            }}
            error={minError}
            helperText={minError ? "Must be less than Max value." : null}
            disabled={!selectedYAxis}
          />
        </FormControl>
      </InputWrapper>

      <InputWrapper>
        <FormControl error variant="outlined" fullWidth>
          <TextField
            name="yAxisRangeMax"
            label="Max"
            aria-label="yAxisRangeMaxInput"
            variant="outlined"
            type="number"
            size="small"
            value={typeof localMax === "number" ? localMax : ""}
            onChange={event => handleChangeMax(event.target.value)}
            InputProps={{
              inputProps: { min: localMin || undefined, step: 0.1 }
            }}
            error={maxError}
            helperText={maxError ? "Must be greater than Min value." : null}
            disabled={!selectedYAxis}
          />
        </FormControl>
      </InputWrapper>
    </AxisRangeWrapper>
  );
};

const AxisRangeWrapper = styled("div")`
  display: flex;
  flex-direction: column;
`;

const SubHeader = styled(Typography)`
  padding-bottom: ${({ theme }) => `${theme.spacing(1)} `};
`;

const InputWrapper = styled("div")`
  padding: ${({ theme }) => `${theme.spacing(1)} 0px`};
`;
